import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../auth/auth.service';
import { ConfirmDialogService } from '../../../features/confirm-dialog/confirm-dialog.service';

/** Human-readable role label: STORE_MANAGER → Store manager. */
export function humanizeRole(role: string | null | undefined): string {
  if (!role) return 'User';
  const words = role.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './user-menu.component.html',
})
export class UserMenuComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  menuOpen = signal(false);
  bellOpen = signal(false);
  private confirmDialog = inject(ConfirmDialogService);

  private user = toSignal(this.authService.user$, { initialValue: null as any });

  displayName = computed(() => this.user()?.name || this.user()?.username || 'User');
  initials = computed(() => {
    const parts = String(this.displayName()).trim().split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] ?? '';
    const b = parts.length > 1 ? parts[parts.length - 1][0] : parts[0]?.[1] ?? '';
    return (a + b).toUpperCase() || 'ZF';
  });
  roleLabel = computed(() => {
    const roles: string[] = (this.user()?.roles ?? [])
      .map((r: any) => r?.role?.name)
      .filter(Boolean);
    return humanizeRole(roles[0] ?? this.authService.getRole());
  });
  storeCount = computed(() => (this.user()?.storeAccess ?? []).length);
  currentStore = computed(() => {
    const id = this.authService.getActiveLocationId();
    const list: any[] = this.user()?.storeAccess ?? [];
    return list.find((a) => a.locationId === id) ?? list[0] ?? null;
  });

  private closeTimer: any = null;

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.bellOpen.set(false);
    this.menuOpen.update((o) => !o);
  }

  toggleBell(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen.set(false);
    this.bellOpen.update((o) => !o);
  }

  closeAll(): void {
    this.menuOpen.set(false);
    this.bellOpen.set(false);
  }

  // Shared confirm dialog: it renders on <body>, so it centres on the
  // screen. An inline modal here sat inside the header, whose
  // backdrop-filter traps position:fixed and pinned it to the top.
  askSignOut(): void {
    this.closeAll();
    this.confirmDialog
      .confirm({
        title: 'Sign out of Zenflow?',
        message:
          'You will need your username and password to get back in. Anything saved stays on the server.',
        confirmText: 'Sign out',
        cancelText: 'Stay signed in',
        tone: 'primary',
        icon: 'bi-box-arrow-right',
        backdrop: true,
      })
      .then((ok) => {
        if (ok) this.signOut();
      });
  }

  signOut(): void {
    this.authService.logout();
    this.router.navigateByUrl('');
  }

  cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeAll(), 320);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeAll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAll();
  }
}
